// Proxy Service for API Testing Tool
import { getApiBaseUrl } from './apiService';
import { buildJsonFileLogFields, isJsonFile } from '../utils/consoleFileLog';
import { DESKTOP_AGENT_NAME } from '../utils/proxyAgentDownload';
import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  REQUEST_TIMEOUT_STORAGE_KEY,
  applyProxyTimeoutHeader,
  appendProxyTimeoutQuery,
  formatRequestTimedOutError,
  resolveProxyFetchTimeoutMs,
  loadStoredRequestTimeoutMs,
  saveStoredRequestTimeoutMs,
} from '../utils/requestTimeout';
import { toAbsoluteWireUrl } from '../utils/queryParamsExecutionUtils';

interface ProxyRequest {
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
}

interface ProxyResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  data: any;
  url: string;
}

/** Single proxy log entry for Console UI (observability only). */
export interface ProxyLogEntry {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  status?: number;
  statusText?: string;
  durationMs?: number;
  error?: string;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBody?: unknown;
  responseBody?: unknown;
}

const MAX_PROXY_LOG_ENTRIES = 200;
let proxyLogIdCounter = 0;

const CUSTOM_PROXY_STORAGE_KEY = 'customProxyUrl';
const PROXY_MODE_STORAGE_KEY = 'proxyMode';
const LOCAL_PROXY_DEFAULT_URL = 'http://localhost:9000';
/** Shorter probe on hosted sites — desktop agent is optional; avoid 5–10s waits before server fallback. */
const HOSTED_LOCAL_AGENT_PROBE_MS = 1500;
const HOSTED_LOCAL_AGENT_DOWN_TTL_MS = 5 * 60 * 1000;

export type ProxyRoutingMode = 'auto' | 'custom';

/** Chromium Local Network Access: annotate loopback so HTTPS pages can reach http://localhost. */
type LocalProxyFetchInit = RequestInit & { targetAddressSpace?: 'local' | 'loopback' | 'private' };

function isBrowserOnRemoteInsecureHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  const isLoopbackHost =
    host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
  return !window.isSecureContext && !isLoopbackHost;
}

function isLocalProxyBaseUrl(baseUrl: string): boolean {
  try {
    const u = new URL(baseUrl);
    const h = u.hostname.toLowerCase();
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost');
  } catch {
    return /localhost|127\.0\.0\.1/i.test(baseUrl);
  }
}

/**
 * Build fetch init for calls to the local proxy agent.
 * - Avoid Content-Type on GETs (forces CORS preflight).
 * - Set targetAddressSpace: 'loopback' for Chromium LNA / mixed-content exemption (HTTPS → http://localhost).
 */
function localProxyFetchInit(init: LocalProxyFetchInit = {}): LocalProxyFetchInit {
  const headers = new Headers(init.headers || {});
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  return {
    mode: 'cors',
    ...init,
    headers,
    // Chromium Local Network Access (Chrome 138+): required for public pages → localhost/127.0.0.1
    targetAddressSpace: init.targetAddressSpace ?? 'loopback',
  };
}

function chromeInsecureLocalProxyMessage(rawError: string): string {
  if (!isBrowserOnRemoteInsecureHost()) {
    return `Connection failed: ${rawError}`;
  }
  return (
    'Connection failed: Chrome/Safari block localhost from HTTP (“Not Secure”) sites. ' +
    'Local Network permission is not enough. Fix: open this app over HTTPS ' +
    '(e.g. https://your-tenant.crudexpert.com or https://your-tenant.dragonflytest.in), ' +
    'then click Allow when the browser asks for local network/loopback. ' +
    'Confirm the agent with http://localhost:9000/health.'
  );
}

/**
 * Cookie mirrors localStorage so the chosen proxy URL survives:
 * - Cross-subdomain navigation (e.g. signup on main domain → tenant subdomain; separate localStorage origins)
 * - localStorage.clear() (e.g. payment success) which does not remove cookies
 */
function getProxySharingCookieDomain(): string | undefined {
  if (typeof window === 'undefined' || !window.location?.hostname) return undefined;
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
    return '.localhost';
  }
  const parts = host.split('.').filter(Boolean);
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return undefined;
}

function readCustomProxyCookie(): string | null {
  try {
    const prefix = `${CUSTOM_PROXY_STORAGE_KEY}=`;
    const hit = document.cookie
      .split('; ')
      .map((p) => p.trim())
      .find((p) => p.startsWith(prefix));
    if (!hit) return null;
    return decodeURIComponent(hit.slice(prefix.length));
  } catch {
    return null;
  }
}

function writeCustomProxyCookie(value: string | null): void {
  try {
    const domain = getProxySharingCookieDomain();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const sameSite = '; SameSite=Lax';
    const path = '; Path=/';
    const dom = domain ? `; Domain=${domain}` : '';
    if (value) {
      document.cookie = `${CUSTOM_PROXY_STORAGE_KEY}=${encodeURIComponent(value)}${path}; Max-Age=31536000${sameSite}${secure}${dom}`;
    } else {
      document.cookie = `${CUSTOM_PROXY_STORAGE_KEY}=${path}; Max-Age=0${sameSite}${secure}${dom}`;
    }
  } catch (e) {
    console.warn('ProxyService: failed to persist proxy URL cookie', e);
  }
}

function readRequestTimeoutCookie(): string | null {
  try {
    const prefix = `${REQUEST_TIMEOUT_STORAGE_KEY}=`;
    const hit = document.cookie
      .split('; ')
      .map((p) => p.trim())
      .find((p) => p.startsWith(prefix));
    if (!hit) return null;
    return decodeURIComponent(hit.slice(prefix.length));
  } catch {
    return null;
  }
}

function writeRequestTimeoutCookie(value: number | null): void {
  try {
    const domain = getProxySharingCookieDomain();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const sameSite = '; SameSite=Lax';
    const path = '; Path=/';
    const dom = domain ? `; Domain=${domain}` : '';
    if (value != null) {
      document.cookie = `${REQUEST_TIMEOUT_STORAGE_KEY}=${encodeURIComponent(String(value))}${path}; Max-Age=31536000${sameSite}${secure}${dom}`;
    } else {
      document.cookie = `${REQUEST_TIMEOUT_STORAGE_KEY}=${path}; Max-Age=0${sameSite}${secure}${dom}`;
    }
  } catch (e) {
    console.warn('ProxyService: failed to persist request timeout cookie', e);
  }
}

class ProxyService {
  // Support for both local and server proxy
  private customProxyUrl: string | null = null;
  private proxyMode: ProxyRoutingMode = 'custom';
  private requestTimeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS;
  private isProxyAvailable: boolean = false;
  private healthCheckInProgress: boolean = false;
  /** Shared in-flight health check so concurrent callers await the real result (not stale false). */
  private healthCheckPromise: Promise<boolean> | null = null;
  /** After a failed localhost:9000 probe on a hosted tenant, skip re-probing until this timestamp. */
  private hostedLocalAgentDownUntilMs = 0;

  // Remove bypass configuration - ALL requests should go through proxy for consistency
  private bypassProxyUrls: string[] = [];

  // Console log (observability only): in-memory buffer + subscribers
  private proxyLogEntries: ProxyLogEntry[] = [];
  private proxyLogListeners = new Set<(entries: ProxyLogEntry[]) => void>();
  private connectionListeners = new Set<(connected: boolean) => void>();

  constructor() {
    // Load custom proxy URL from localStorage
    this.loadProxyConfiguration();
    this.repairStoredProxyUrl();
    const proxyUrl = this.getProxyBaseUrl();
    console.log(`ProxyService: Initialized, proxy URL: ${proxyUrl}`);
    this.loadBypassConfiguration();
  }

  private isLocalhostEnvironment(): boolean {
    try {
      const host = window.location.hostname.toLowerCase();
      return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
    } catch {
      return false;
    }
  }

  private getPageHostname(): string {
    try {
      return window.location.hostname.toLowerCase();
    } catch {
      return '';
    }
  }

  /** True when the page is served from a hosted CRUD Expert tenant (not local dev). */
  private isHostedAppEnvironment(): boolean {
    const host = this.getPageHostname();
    return !!host && !this.isLocalhostEnvironment();
  }

  /**
   * The local Node proxy agent always runs on the user's machine at localhost:9000.
   * Saved URLs that point at the page hostname, API base, or Spring /api/proxy must be migrated.
   */
  private normalizeStoredProxyUrl(raw: string | null | undefined): string {
    const fallback = LOCAL_PROXY_DEFAULT_URL;
    if (!raw || typeof raw !== 'string') return fallback;
    const trimmed = raw.trim();
    if (!trimmed) return fallback;

    try {
      const u = new URL(trimmed);
      const host = u.hostname.toLowerCase();
      const pageHost = this.getPageHostname();
      const path = (u.pathname || '').toLowerCase();

      // Spring backend proxy uses /api/proxy?url= — not the Node /proxy/{encodedUrl} API.
      if (path.includes('/api') || path.includes('/proxy')) {
        console.warn('ProxyService: Migrating API/backend proxy URL to local agent:', trimmed);
        return fallback;
      }

      // Page hostname on :9000 is not the local agent (browser cannot reach tenant host as localhost proxy).
      if (
        pageHost &&
        (host === pageHost || host.endsWith('.crudexpert.com') || host === 'crudexpert.com') &&
        (u.port === '9000' || u.port === '' || u.port === '80' || u.port === '443')
      ) {
        console.warn('ProxyService: Migrating same-origin proxy URL to localhost:9000:', trimmed);
        return fallback;
      }

      // Proxy agent always listens on localhost:9000 (not fly.localhost:9000 or other *.localhost).
      if (host.endsWith('.localhost') && (u.port === '9000' || u.port === '')) {
        console.warn('ProxyService: Migrating *.localhost proxy URL to localhost:9000:', trimmed);
        return fallback;
      }

      // Strip trailing slashes for consistency.
      let normalized = u.toString();
      if (normalized.endsWith('/')) {
        normalized = normalized.replace(/\/+$/, '');
      }
      return normalized;
    } catch {
      console.warn('ProxyService: Invalid stored proxy URL, using default:', trimmed);
      return fallback;
    }
  }

  /** Fix cookie/localStorage if an old session saved the wrong proxy URL. */
  public repairStoredProxyUrl(): boolean {
    const before = this.customProxyUrl;
    const normalized = this.normalizeStoredProxyUrl(before ?? LOCAL_PROXY_DEFAULT_URL);
    if (before !== normalized) {
      this.customProxyUrl = normalized;
      this.saveProxyConfiguration();
      this.setConnectionAvailable(false);
      console.log('ProxyService: Repaired stored proxy URL:', before, '→', normalized);
      return true;
    }
    return false;
  }

  /** URL to persist from Proxy Settings on hosted apps (local agent unless remote enterprise LAN proxy). */
  public resolveProxyUrlForSave(input: string): string {
    if (this.isLocalhostEnvironment()) {
      return this.getLocalDevProxyBaseUrl();
    }
    const normalized = this.normalizeStoredProxyUrl(input);
    try {
      const u = new URL(normalized);
      const host = u.hostname.toLowerCase();
      const isLoopback = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
      const isPrivateLan =
        /^(10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(host);
      if (isLoopback || isPrivateLan) {
        return normalized;
      }
    } catch {
      /* fall through */
    }
    return LOCAL_PROXY_DEFAULT_URL;
  }

  /**
   * Base URL for the local Node proxy on this machine (always localhost:9000).
   */
  public getLocalDevProxyBaseUrl(): string {
    return LOCAL_PROXY_DEFAULT_URL;
  }

  // Determine proxy URL based on configuration or environment
  private getProxyBaseUrl(): string {
    // Local dev (*.localhost, localhost): proxy agent runs on http://localhost:9000
    if (this.isLocalhostEnvironment()) {
      return LOCAL_PROXY_DEFAULT_URL;
    }

    // If user has set a custom proxy URL, use it (after migration from bad saved values)
    if (this.customProxyUrl) {
      const normalized = this.normalizeStoredProxyUrl(this.customProxyUrl);
      if (normalized !== this.customProxyUrl) {
        this.customProxyUrl = normalized;
        this.saveProxyConfiguration();
      }
      console.log('ProxyService: Using custom proxy URL:', this.customProxyUrl);
      return this.customProxyUrl;
    }

    // New sessions / signups: use the static local proxy URL (same as dev), not `${currentHost}:9000`.
    // Users who need a different proxy can set it in Proxy Server Settings (Edit → Save).
    return LOCAL_PROXY_DEFAULT_URL;
  }

  private readStoredRequestTimeoutMs(): number {
    return loadStoredRequestTimeoutMs(readRequestTimeoutCookie);
  }

  private persistRequestTimeoutMs(): void {
    try {
      saveStoredRequestTimeoutMs(this.requestTimeoutMs, writeRequestTimeoutCookie);
    } catch (error) {
      console.warn('ProxyService: Failed to save request timeout');
    }
  }

  private getDefaultProxyModeForEnvironment(): ProxyRoutingMode {
    if (this.isLocalhostEnvironment()) {
      return 'custom';
    }
    const envDefault = process.env.REACT_APP_DEFAULT_PROXY_MODE;
    if (envDefault === 'custom') return 'custom';
    if (envDefault === 'auto') return 'auto';
    return 'auto';
  }

  private readStoredProxyMode(): ProxyRoutingMode {
    try {
      const stored = localStorage.getItem(PROXY_MODE_STORAGE_KEY);
      if (stored === 'auto' || stored === 'custom') {
        return stored;
      }
    } catch {
      /* ignore */
    }
    return this.getDefaultProxyModeForEnvironment();
  }

  private getLocalAgentHealthProbeMs(): number {
    return this.isHostedAppEnvironment() ? HOSTED_LOCAL_AGENT_PROBE_MS : 5000;
  }

  private hostedLocalAgentRecentlyDown(): boolean {
    return this.isHostedAppEnvironment() && Date.now() < this.hostedLocalAgentDownUntilMs;
  }

  private markHostedLocalAgentDown(): void {
    if (this.isHostedAppEnvironment()) {
      this.hostedLocalAgentDownUntilMs = Date.now() + HOSTED_LOCAL_AGENT_DOWN_TTL_MS;
    }
  }

  private markHostedLocalAgentUp(): void {
    this.hostedLocalAgentDownUntilMs = 0;
  }

  public getServerProxyUrl(): string {
    return `${getApiBaseUrl()}/proxy`;
  }

  public getProxyMode(): ProxyRoutingMode {
    return this.proxyMode;
  }

  public setProxyMode(mode: ProxyRoutingMode): void {
    this.proxyMode = mode === 'auto' ? 'auto' : 'custom';
    this.saveProxyConfiguration();
    this.setConnectionAvailable(false);
    console.log('ProxyService: Proxy mode updated to:', this.proxyMode);
  }

  private async probeServerProxy(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
      const headers: Record<string, string> = {};
      const token = this.getAuthToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${getApiBaseUrl()}/test/health`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
      return response.status > 0;
    } catch {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Load proxy configuration from localStorage
  private loadProxyConfiguration() {
    try {
      this.requestTimeoutMs = this.readStoredRequestTimeoutMs();
      this.proxyMode = this.readStoredProxyMode();
      try {
        if (!localStorage.getItem(PROXY_MODE_STORAGE_KEY) && this.isHostedAppEnvironment()) {
          this.saveProxyConfiguration();
        }
      } catch {
        /* ignore quota */
      }
      const fromLs = localStorage.getItem(CUSTOM_PROXY_STORAGE_KEY);
      const fromCookie = readCustomProxyCookie();
      const savedProxyUrl = fromLs || fromCookie;
      if (this.isLocalhostEnvironment()) {
        this.customProxyUrl = LOCAL_PROXY_DEFAULT_URL;
        localStorage.setItem(CUSTOM_PROXY_STORAGE_KEY, this.customProxyUrl);
        writeCustomProxyCookie(this.customProxyUrl);
        console.log('ProxyService: Enforced local dev proxy URL', this.customProxyUrl);
        return;
      }
      if (savedProxyUrl) {
        const normalized = this.normalizeStoredProxyUrl(savedProxyUrl);
        this.customProxyUrl = normalized;
        if (normalized !== savedProxyUrl) {
          console.warn('ProxyService: Migrated stored proxy URL on load:', savedProxyUrl, '→', normalized);
        }
        // Restore localStorage on this origin when value came from cookie (e.g. new subdomain after signup).
        if (!fromLs || normalized !== savedProxyUrl) {
          try {
            localStorage.setItem(CUSTOM_PROXY_STORAGE_KEY, this.customProxyUrl);
          } catch {
            /* ignore quota */
          }
        }
        writeCustomProxyCookie(this.customProxyUrl);
        console.log('ProxyService: Loaded custom proxy URL from storage:', this.customProxyUrl);
      } else if (this.isHostedAppEnvironment()) {
        this.customProxyUrl = LOCAL_PROXY_DEFAULT_URL;
        this.saveProxyConfiguration();
      }
    } catch (error) {
      console.warn('ProxyService: Failed to load proxy configuration');
    }
  }

  // Save proxy configuration to localStorage
  private saveProxyConfiguration() {
    try {
      localStorage.setItem(PROXY_MODE_STORAGE_KEY, this.proxyMode);
      if (this.customProxyUrl) {
        localStorage.setItem(CUSTOM_PROXY_STORAGE_KEY, this.customProxyUrl);
        writeCustomProxyCookie(this.customProxyUrl);
      } else {
        localStorage.removeItem(CUSTOM_PROXY_STORAGE_KEY);
        writeCustomProxyCookie(null);
      }
      this.persistRequestTimeoutMs();
    } catch (error) {
      console.warn('ProxyService: Failed to save proxy configuration');
    }
  }

  // Public method to set custom proxy URL
  public setProxyUrl(url: string | null) {
    // Local dev: proxy agent is always http://localhost:9000
    if (this.isLocalhostEnvironment()) {
      this.customProxyUrl = LOCAL_PROXY_DEFAULT_URL;
    } else if (url) {
      this.customProxyUrl = this.resolveProxyUrlForSave(url);
    } else {
      this.customProxyUrl = null;
    }
    this.saveProxyConfiguration();
    // Reset availability check when proxy URL changes
    this.setConnectionAvailable(false);
    console.log('ProxyService: Proxy URL updated to:', url || 'auto-detect');
  }

  public getDesktopAgentUrl(): string {
    return this.getProxyBaseUrl();
  }

  // Public method to get current proxy URL
  public getProxyUrl(): string {
    if (this.proxyMode === 'auto') {
      return this.getServerProxyUrl();
    }
    return this.getProxyBaseUrl();
  }

  /** Effective configured Request Timeout in ms. Re-reads storage so the next request is not stale. */
  public getRequestTimeoutMs(): number {
    this.requestTimeoutMs = this.readStoredRequestTimeoutMs();
    return this.requestTimeoutMs;
  }

  public setRequestTimeoutMs(ms: number): void {
    this.requestTimeoutMs = saveStoredRequestTimeoutMs(ms, writeRequestTimeoutCookie);
  }

  /** Snapshot the effective timeout for one request (normal path + special-case floors). */
  public getEffectiveProxyFetchTimeoutMs(requestData: Pick<ProxyRequest, 'timeout' | 'body'> = {}): number {
    return this.computeProxyFetchTimeoutMs(requestData as ProxyRequest);
  }

  // Public method to clear custom proxy URL (revert to auto-detect)
  public clearCustomProxyUrl() {
    this.setProxyUrl(null);
  }

  /** Truncate request body for log display to avoid huge payloads in the console list. */
  private async safeBodyForLog(body: unknown): Promise<unknown> {
    if (body === undefined || body === null) return body;
    const multipartOrBinary = await this.serializeMultipartOrBinaryBodyForLog(body);
    if (multipartOrBinary != null) return multipartOrBinary;
    const max = 2000;
    try {
      const str = typeof body === 'string' ? body : JSON.stringify(body);
      if (typeof body === 'string') {
        const parsedMultipart = this.trySerializePersistedBodyStringForLog(body);
        if (parsedMultipart != null) return parsedMultipart;
      }
      return str.length <= max ? body : str.slice(0, max) + '…';
    } catch {
      return '[non-serializable]';
    }
  }

  /**
   * Resolve leftover $function(...) tokens in string URL/headers/body before the proxy hop.
   * Ensures newly created JSON requests evaluate functions even if a Collections path skipped async resolution.
   */
  private async resolveFunctionsInProxyRequest(requestData: ProxyRequest): Promise<ProxyRequest> {
    const body = requestData.body;

    try {
      const { ensureFunctionsResolvedInText } = await import('./functionService');
      let changed = false;
      let url = requestData.url;
      if (typeof url === 'string' && url.includes('$')) {
        url = await ensureFunctionsResolvedInText(url);
        changed = true;
      }

      let headers = requestData.headers;
      if (headers && typeof headers === 'object') {
        let headersChanged = false;
        const nextHeaders: Record<string, string> = { ...headers };
        for (const [key, value] of Object.entries(nextHeaders)) {
          if (typeof value === 'string' && value.includes('$')) {
            nextHeaders[key] = await ensureFunctionsResolvedInText(value);
            headersChanged = true;
          }
        }
        if (headersChanged) {
          headers = nextHeaders;
          changed = true;
        }
      }

      let nextBody = body;

      // Multipart: resolve leftover $fn in text fields (file parts unchanged).
      if (body != null && this.isFormDataBody(body)) {
        const form = body as FormData;
        const rebuilt = new FormData();
        let formChanged = false;
        const entries = Array.from(form.entries());
        for (const [key, value] of entries) {
          let nextKey = key;
          if (typeof nextKey === 'string' && nextKey.includes('$')) {
            nextKey = await ensureFunctionsResolvedInText(nextKey);
            formChanged = true;
          }
          if (typeof value === 'string' && value.includes('$')) {
            rebuilt.append(nextKey, await ensureFunctionsResolvedInText(value));
            formChanged = true;
          } else {
            rebuilt.append(nextKey, value);
          }
        }
        if (formChanged) {
          nextBody = rebuilt;
          changed = true;
        }
      } else if (
        body != null &&
        ((typeof File !== 'undefined' && body instanceof File) ||
          body instanceof Blob ||
          body instanceof ArrayBuffer)
      ) {
        // Binary upload — no $function tokens inside bytes.
      } else if (typeof body === 'string') {
        const contentType = String(
          headers?.['Content-Type'] || headers?.['content-type'] || ''
        ).toLowerCase();
        const isUrlEncoded = contentType.includes('application/x-www-form-urlencoded');
        if (isUrlEncoded && (body.includes('$') || body.includes('%24'))) {
          const params = new URLSearchParams(body);
          const rebuilt = new URLSearchParams();
          let encodedChanged = false;
          for (const [key, value] of Array.from(params.entries())) {
            let k = key;
            let v = value;
            if (k.includes('$')) {
              k = await ensureFunctionsResolvedInText(k);
              encodedChanged = true;
            }
            if (v.includes('$')) {
              v = await ensureFunctionsResolvedInText(v);
              encodedChanged = true;
            }
            rebuilt.append(k, v);
          }
          if (encodedChanged) {
            nextBody = rebuilt.toString();
            changed = true;
          }
        } else if (body.includes('$')) {
          nextBody = await ensureFunctionsResolvedInText(body);
          changed = true;
        }
      }

      if (!changed) {
        return requestData;
      }
      return {
        ...requestData,
        url,
        headers,
        body: nextBody,
      };
    } catch (err) {
      // Unknown / missing $functions must fail the request (same as Collections Send).
      // Swallowing them used to send "$isoTimestampSSSS" as-is and the API often returned 403,
      // which the AI Agent then misdiagnosed as an auth/permission problem.
      const message = err instanceof Error ? err.message : String(err);
      if (/unknown function/i.test(message)) {
        throw err instanceof Error ? err : new Error(message);
      }
      console.error('ProxyService: function resolution failed; sending body as-is', err);
      return requestData;
    }
  }

  /** Preserve full response body for console display (Console.tsx applies preview/size limits in the UI). */
  private async safeResponseBodyForLog(body: unknown): Promise<unknown> {
    if (body === undefined || body === null) return body;
    const multipartOrBinary = await this.serializeMultipartOrBinaryBodyForLog(body);
    if (multipartOrBinary != null) return multipartOrBinary;
    try {
      if (typeof body === 'string') {
        if (body.length > 100 * 1024) {
          return `[response body ${body.length} chars omitted from console log]`;
        }
        const parsedMultipart = this.trySerializePersistedBodyStringForLog(body);
        if (parsedMultipart != null) return parsedMultipart;
      }
      return body;
    } catch {
      return '[non-serializable]';
    }
  }

  private formatBytesForLog(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  private async serializeMultipartOrBinaryBodyForLog(body: unknown): Promise<unknown | null> {
    if (this.isFormDataBody(body)) {
      return this.serializeFormDataForLog(body as FormData);
    }
    if (typeof File !== 'undefined' && body instanceof File) {
      return this.serializeBinaryFileForLog(body);
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      return this.serializeBinaryBlobForLog(body);
    }
    if (body instanceof ArrayBuffer) {
      return {
        type: 'binary',
        contentType: 'application/octet-stream',
        sizeBytes: body.byteLength,
        size: this.formatBytesForLog(body.byteLength),
        note: 'Binary buffer content omitted from console log.',
      };
    }
    if (
      typeof body === 'object' &&
      body !== null &&
      typeof (body as Blob).slice === 'function' &&
      'size' in (body as Blob)
    ) {
      const blob = body as Blob & { name?: string };
      const fileName =
        typeof blob.name === 'string' && blob.name.trim() ? blob.name : '(blob)';
      const asFileLike =
        typeof File !== 'undefined' && blob instanceof File
          ? blob
          : ({ name: fileName, type: blob.type || '', size: blob.size } as File);
      const base: Record<string, unknown> = {
        type: 'binary',
        fileName,
        contentType: blob.type || 'application/octet-stream',
        sizeBytes: blob.size,
        size: this.formatBytesForLog(blob.size),
      };
      if (typeof File !== 'undefined' && blob instanceof File && isJsonFile(blob)) {
        const jsonFields = await buildJsonFileLogFields(blob);
        if (jsonFields) Object.assign(base, jsonFields);
        return base;
      }
      if (isJsonFile(asFileLike) && typeof (blob as Blob).text === 'function') {
        try {
          const fakeFile = new File([blob], fileName, { type: blob.type || 'application/json' });
          const jsonFields = await buildJsonFileLogFields(fakeFile);
          if (jsonFields) Object.assign(base, jsonFields);
          return base;
        } catch {
          // fall through to metadata-only
        }
      }
      base.note = 'Binary file content omitted from console log.';
      return base;
    }
    return null;
  }

  private async serializeFormDataForLog(formData: FormData): Promise<unknown> {
    const fields: Array<Record<string, unknown>> = [];
    let totalBytes = 0;
    let includedJsonFileContent = false;
    const entries = Array.from(formData.entries());
    for (const [key, value] of entries) {
      if (typeof Blob !== 'undefined' && value instanceof Blob) {
        const fileName =
          typeof File !== 'undefined' && value instanceof File && value.name
            ? value.name
            : String(key);
        const contentType = value.type || 'application/octet-stream';
        totalBytes += value.size;
        const field: Record<string, unknown> = {
          key,
          fieldType: 'file',
          fileName,
          contentType,
          sizeBytes: value.size,
          size: this.formatBytesForLog(value.size),
        };
        if (typeof File !== 'undefined' && value instanceof File) {
          if (value.lastModified) {
            field.lastModified = new Date(value.lastModified).toISOString();
          }
          if (isJsonFile(value)) {
            const jsonFields = await buildJsonFileLogFields(value);
            if (jsonFields) {
              Object.assign(field, jsonFields);
              if (jsonFields.fileContent != null || jsonFields.contentTooLarge) {
                includedJsonFileContent = true;
              }
            }
          }
        } else if (isJsonFile({ name: fileName, type: contentType })) {
          try {
            const fakeFile = new File([value], fileName, { type: contentType });
            const jsonFields = await buildJsonFileLogFields(fakeFile);
            if (jsonFields) {
              Object.assign(field, jsonFields);
              if (jsonFields.fileContent != null || jsonFields.contentTooLarge) {
                includedJsonFileContent = true;
              }
            }
          } catch {
            // metadata only
          }
        }
        fields.push(field);
      } else {
        const text = String(value);
        totalBytes += new TextEncoder().encode(text).length;
        const field: Record<string, unknown> = {
          key,
          fieldType: 'text',
          value: text,
        };
        if (text.length > 2000) {
          field.value = text.slice(0, 2000) + '…';
          field.truncated = true;
          field.originalLength = text.length;
        }
        fields.push(field);
      }
    }
    const result: Record<string, unknown> = {
      type: 'form-data',
      fieldCount: fields.length,
      totalSizeBytes: totalBytes,
      totalSize: this.formatBytesForLog(totalBytes),
      fields,
    };
    if (!includedJsonFileContent) {
      result.note = 'File field contents are omitted from the console log.';
    }
    return result;
  }

  private async serializeBinaryFileForLog(file: File): Promise<unknown> {
    const base: Record<string, unknown> = {
      type: 'binary',
      fileName: file.name || '(unnamed file)',
      contentType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      size: this.formatBytesForLog(file.size),
      lastModified: file.lastModified ? new Date(file.lastModified).toISOString() : undefined,
    };
    if (isJsonFile(file)) {
      const jsonFields = await buildJsonFileLogFields(file);
      if (jsonFields) Object.assign(base, jsonFields);
      return base;
    }
    base.note = 'Binary file content omitted from console log.';
    return base;
  }

  private async serializeBinaryBlobForLog(blob: Blob): Promise<unknown> {
    const base: Record<string, unknown> = {
      type: 'binary',
      fileName: '(blob)',
      contentType: blob.type || 'application/octet-stream',
      sizeBytes: blob.size,
      size: this.formatBytesForLog(blob.size),
    };
    if (isJsonFile({ name: '', type: blob.type })) {
      try {
        const fakeFile = new File([blob], 'blob.json', {
          type: blob.type || 'application/json',
        });
        const jsonFields = await buildJsonFileLogFields(fakeFile);
        if (jsonFields) Object.assign(base, jsonFields);
        return base;
      } catch {
        // fall through
      }
    }
    base.note = 'Binary blob content omitted from console log.';
    return base;
  }

  private trySerializePersistedBodyStringForLog(raw: string): unknown | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        const rows = parsed.filter(
          (row): row is Record<string, unknown> =>
            row != null && typeof row === 'object' && 'key' in row
        );
        if (rows.length > 0) {
          const fields = rows.map((row) => {
            const key = String(row.key ?? '').trim();
            const rowType = String(row.type ?? row.fieldType ?? 'text').toLowerCase();
            if (rowType === 'file') {
              const fileName = String(row.value ?? row.fileName ?? (key || '(file)')).trim();
              const contentType = String(row.mimeType ?? row.contentType ?? 'application/octet-stream');
              const sizeBytes =
                typeof row.size === 'number'
                  ? row.size
                  : typeof row.sizeBytes === 'number'
                    ? row.sizeBytes
                    : undefined;
              return {
                key,
                fieldType: 'file',
                fileName,
                contentType,
                ...(sizeBytes != null
                  ? { sizeBytes, size: this.formatBytesForLog(sizeBytes) }
                  : {}),
              };
            }
            const value = String(row.value ?? '');
            const field: Record<string, unknown> = {
              key,
              fieldType: 'text',
              value: value.length > 2000 ? value.slice(0, 2000) + '…' : value,
            };
            if (value.length > 2000) {
              field.truncated = true;
              field.originalLength = value.length;
            }
            return field;
          });
          return {
            type: 'form-data',
            fieldCount: fields.length,
            fields,
            note: 'Persisted form-data rows (file contents omitted from console log).',
          };
        }
      }
      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>;
        if (obj.__binaryMeta === true || obj.__binaryData__ === true || obj.type === 'binary-meta') {
          const sizeBytes =
            typeof obj.size === 'number'
              ? obj.size
              : typeof obj.sizeBytes === 'number'
                ? obj.sizeBytes
                : undefined;
          return {
            type: 'binary',
            fileName: String(obj.fileName ?? '(binary file)'),
            contentType: String(obj.mimeType ?? obj.contentType ?? 'application/octet-stream'),
            ...(sizeBytes != null
              ? { sizeBytes, size: this.formatBytesForLog(sizeBytes) }
              : {}),
            note: 'Binary file metadata (content omitted from console log).',
          };
        }
      }
    } catch {
      return null;
    }
    return null;
  }

  /** Add a proxy log entry and notify subscribers (for Console UI). */
  private addProxyLogEntry(entry: Omit<ProxyLogEntry, 'id' | 'timestamp'>) {
    const full: ProxyLogEntry = {
      ...entry,
      id: `proxy-log-${++proxyLogIdCounter}`,
      timestamp: Date.now(),
    };
    this.proxyLogEntries = [full, ...this.proxyLogEntries].slice(0, MAX_PROXY_LOG_ENTRIES);
    this.proxyLogListeners.forEach((cb) => cb(this.proxyLogEntries));
  }

  /** Get current proxy log entries (for Console UI). */
  public getProxyLogEntries(): ProxyLogEntry[] {
    return [...this.proxyLogEntries];
  }

  /** Subscribe to proxy log updates. Returns unsubscribe. */
  public subscribeToProxyLogs(callback: (entries: ProxyLogEntry[]) => void): () => void {
    this.proxyLogListeners.add(callback);
    callback(this.proxyLogEntries);
    return () => this.proxyLogListeners.delete(callback);
  }

  /** Clear all proxy log entries and notify subscribers. */
  public clearProxyLogs(): void {
    this.proxyLogEntries = [];
    this.proxyLogListeners.forEach((cb) => cb(this.proxyLogEntries));
  }

  /** Ingest logs produced outside proxyService (e.g. AI Agent backend runs). */
  public ingestExternalLogs(
    logs: Array<{
      method?: string;
      url?: string;
      status?: number;
      statusText?: string;
      error?: string | null;
      responseHeaders?: unknown;
      responseBody?: unknown;
    }>
  ): void {
    if (!Array.isArray(logs) || logs.length === 0) return;
    for (const log of logs) {
      if (!log || !log.method || !log.url) continue;
      const parsedHeaders: Record<string, string> | undefined = (() => {
        if (log.responseHeaders && typeof log.responseHeaders === 'object') {
          return log.responseHeaders as Record<string, string>;
        }
        if (typeof log.responseHeaders === 'string' && log.responseHeaders.trim()) {
          try {
            const obj = JSON.parse(log.responseHeaders);
            if (obj && typeof obj === 'object') {
              return obj as Record<string, string>;
            }
          } catch {
            return undefined;
          }
        }
        return undefined;
      })();
      this.addProxyLogEntry({
        method: log.method,
        url: log.url,
        status: typeof log.status === 'number' ? log.status : undefined,
        statusText: typeof log.statusText === 'string' ? log.statusText : undefined,
        error: typeof log.error === 'string' ? log.error : undefined,
        responseHeaders: parsedHeaders,
        responseBody: log.responseBody,
      });
    }
  }

  private loadBypassConfiguration() {
    try {
      const savedConfig = localStorage.getItem('proxyBypassUrls');
      if (savedConfig) {
        this.bypassProxyUrls = JSON.parse(savedConfig);
      }
    } catch (error) {
      console.warn('ProxyService: Failed to load bypass configuration, using defaults');
    }
  }

  private saveBypassConfiguration() {
    try {
      localStorage.setItem('proxyBypassUrls', JSON.stringify(this.bypassProxyUrls));
    } catch (error) {
      console.warn('ProxyService: Failed to save bypass configuration');
    }
  }

  public addBypassUrl(pattern: string) {
    if (!this.bypassProxyUrls.includes(pattern)) {
      this.bypassProxyUrls.push(pattern);
      this.saveBypassConfiguration();
    }
  }

  public removeBypassUrl(pattern: string) {
    const index = this.bypassProxyUrls.indexOf(pattern);
    if (index > -1) {
      this.bypassProxyUrls.splice(index, 1);
      this.saveBypassConfiguration();
    }
  }

  public getBypassUrls(): string[] {
    return [...this.bypassProxyUrls];
  }

  private shouldBypassProxy(url: string): boolean {
    console.log('🔍 shouldBypassProxy called with URL:', url);
    console.log('📋 Current bypass patterns:', this.bypassProxyUrls);
    
    // ALWAYS return false to ensure ALL requests go through proxy
    console.log('🚫 Should bypass proxy: FALSE (all requests through proxy)');
    return false;
  }

  private getAuthToken(): string | null {
    try {
      return localStorage.getItem('token') || sessionStorage.getItem('token');
    } catch {
      return null;
    }
  }

  /**
   * When the local Node proxy agent is unavailable on a hosted tenant, route through
   * the Spring backend proxy (same origin /api/proxy) so test runs still work.
   */
  private async sendRequestViaBackendProxy(requestData: ProxyRequest): Promise<ProxyResponse> {
    const proxyFetchTimeoutMs = this.computeProxyFetchTimeoutMs(requestData);
    const apiBase = getApiBaseUrl();
    const proxyUrl = new URL(`${apiBase}/proxy`);
    proxyUrl.searchParams.set('url', requestData.url);
    proxyUrl.searchParams.set('method', requestData.method || 'GET');

    const headers: Record<string, string> = { ...(requestData.headers ?? {}) };
    const token = this.getAuthToken();
    if (token && !headers.Authorization && !headers.authorization) {
      headers.Authorization = `Bearer ${token}`;
    }

    let body: string | undefined;
    const isFormData = this.isFormDataBody(requestData.body);
    const methodUpper = String(requestData.method || 'GET').toUpperCase();
    const allowRequestBody =
      methodUpper !== 'GET' && methodUpper !== 'HEAD' && methodUpper !== 'OPTIONS';
    if (allowRequestBody && requestData.body !== undefined && requestData.body !== null) {
      if (isFormData) {
        throw new Error(
          `File uploads require the ${DESKTOP_AGENT_NAME} (http://localhost:9000). Install and start it, then retry.`,
        );
      }
      if (typeof requestData.body === 'string') {
        body = requestData.body;
      } else if (
        requestData.body instanceof Blob ||
        requestData.body instanceof ArrayBuffer ||
        (typeof File !== 'undefined' && requestData.body instanceof File)
      ) {
        throw new Error(
          `Binary uploads require the ${DESKTOP_AGENT_NAME} (http://localhost:9000). Install and start it, then retry.`,
        );
      } else {
        body = JSON.stringify(requestData.body);
        if (!headers['Content-Type'] && !headers['content-type']) {
          headers['Content-Type'] = 'application/json';
        }
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), proxyFetchTimeoutMs);
    const startTime = Date.now();
    try {
      const response = await fetch(proxyUrl.toString(), {
        method: requestData.method,
        headers,
        body,
        signal: controller.signal,
        credentials: 'include',
        mode: 'cors',
      });
      clearTimeout(timeoutId);
      const responseText = await response.text();
      const contentType = response.headers.get('content-type') || '';
      let responseData: unknown = responseText;
      if (contentType.includes('application/json') && responseText.length <= 2 * 1024 * 1024) {
        try {
          responseData = JSON.parse(responseText);
        } catch {
          responseData = responseText;
        }
      }
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
      const endTime = Date.now();
      this.addProxyLogEntry({
        method: requestData.method,
        url: requestData.url,
        status: response.status,
        statusText: response.statusText,
        durationMs: endTime - startTime,
        requestHeaders: requestData.headers,
        responseHeaders,
        requestBody: await this.safeBodyForLog(requestData.body),
        responseBody: await this.safeResponseBodyForLog(responseData),
      });
      return {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        data: responseData,
        url: requestData.url,
      };
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error(formatRequestTimedOutError(proxyFetchTimeoutMs));
      }
      throw error;
    }
  }

  private async checkProxyAvailability(): Promise<boolean> {
    // Concurrent callers (e.g. React StrictMode remount on local) must await the same check.
    // Returning stale isProxyAvailable=false caused false redirects to /proxy-settings.
    if (this.healthCheckPromise) {
      console.log('ProxyService: Health check already in progress, waiting...');
      return this.healthCheckPromise;
    }

    this.healthCheckInProgress = true;
    this.healthCheckPromise = (async (): Promise<boolean> => {
      if (this.proxyMode === 'auto') {
        try {
          const ok = await this.probeServerProxy();
          this.setConnectionAvailable(ok);
          console.log(
            ok
              ? '✅ Server proxy is available'
              : '❌ Server proxy is not available'
          );
          return ok;
        } finally {
          this.healthCheckInProgress = false;
          this.healthCheckPromise = null;
        }
      }

      console.log('ProxyService: Checking proxy availability at:', `${this.getProxyBaseUrl()}/health`);

      const isValidProxyHealthPayload = (data: any): boolean => {
        if (!data || typeof data !== 'object') return false;
        const status = String(data.status || '').toUpperCase();
        const message = String(data.message || '').toLowerCase();
        return status === 'OK' && message.includes('api testing proxy server');
      };

      const probeMs = this.getLocalAgentHealthProbeMs();

      const checkHealthAt = async (baseUrl: string): Promise<boolean> => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), probeMs);
        try {
          const response = await fetch(
            `${baseUrl}/health`,
            localProxyFetchInit({
              method: 'GET',
              signal: controller.signal,
            })
          );
          if (!response.ok) return false;
          const data = await response.json().catch(() => null);
          return isValidProxyHealthPayload(data);
        } finally {
          clearTimeout(timeoutId);
        }
      };

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), probeMs);

        const response = await fetch(
          `${this.getProxyBaseUrl()}/health`,
          localProxyFetchInit({
            method: 'GET',
            signal: controller.signal,
          })
        );

        clearTimeout(timeoutId);

        console.log('ProxyService: Health check response status:', response.status);
        console.log('ProxyService: Health check response ok:', response.ok);

        if (response.ok) {
          const healthData = await response.json();
          console.log('ProxyService: Health check data:', healthData);
          if (isValidProxyHealthPayload(healthData)) {
            this.setConnectionAvailable(true);
            this.markHostedLocalAgentUp();
            console.log('✅ API Testing Proxy Server is available');
          } else {
            this.setConnectionAvailable(false);
            this.markHostedLocalAgentDown();
            throw new Error('Health endpoint responded, but it is not API Testing Proxy Server');
          }
        } else {
          this.setConnectionAvailable(false);
          this.markHostedLocalAgentDown();
          console.log('❌ API Testing Proxy Server is not responding properly');
        }
      } catch (error: any) {
        console.log('❌ API Testing Proxy Server health check failed');
        console.log('ProxyService: Health check error:', error.message);
        console.log('ProxyService: Error type:', error.name);
        if (this.isHostedAppEnvironment()) {
          this.setConnectionAvailable(false);
          this.markHostedLocalAgentDown();
          console.log('❌ API Testing Proxy Server is not available (hosted — will use server proxy)');
        } else {
          // The agent is fixed on http://localhost:9000 (no port fallback). A single timeout
          // must not mark it disconnected — Windows health checks flap under load, and a
          // long port scan used to hold the shared check so the UI stayed down until
          // Proxy Settings ran a fresh request.
          const retryOk = await checkHealthAt(this.getProxyBaseUrl());
          this.setConnectionAvailable(retryOk);
          if (retryOk) {
            this.markHostedLocalAgentUp();
            console.log('✅ API Testing Proxy Server is available (retry)');
          } else {
            console.log('❌ API Testing Proxy Server is not available');
          }
        }
      } finally {
        this.healthCheckInProgress = false;
        this.healthCheckPromise = null;
      }

      return this.isProxyAvailable;
    })();

    return this.healthCheckPromise;
  }

  public isProxyInstalled(): boolean {
    return this.isProxyAvailable;
  }

  private setConnectionAvailable(available: boolean): void {
    const changed = this.isProxyAvailable !== available;
    this.isProxyAvailable = available;
    if (changed) {
      this.connectionListeners.forEach((listener) => {
        try {
          listener(available);
        } catch {
          /* ignore listener errors */
        }
      });
    }
  }

  /** Subscribe to proxy agent connect/disconnect changes. */
  public subscribeToConnectionStatus(listener: (connected: boolean) => void): () => void {
    this.connectionListeners.add(listener);
    listener(this.isProxyAvailable);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  /** FormData/file uploads need far longer than the default 30s so the proxy can finish like Postman. */
  private static readonly MULTIPART_DIRECT_MAX_BYTES = 5 * 1024 * 1024;

  private estimateUploadBodyBytes(body: unknown): number | null {
    if (body == null) return null;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      let total = 0;
      body.forEach((value) => {
        if (value instanceof Blob) total += value.size;
        else total += new TextEncoder().encode(String(value)).length;
      });
      return total;
    }
    if (typeof File !== 'undefined' && body instanceof File) return body.size;
    if (body instanceof Blob) return body.size;
    if (body instanceof ArrayBuffer) return body.byteLength;
    if (
      typeof body === 'object' &&
      body !== null &&
      typeof (body as Blob).slice === 'function' &&
      'size' in (body as Blob)
    ) {
      return (body as Blob).size;
    }
    return null;
  }

  private isFormDataBody(body: unknown): boolean {
    if (body == null) return false;
    if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
    if (typeof body === 'object') {
      const o = body as Record<string, unknown>;
      return typeof o.append === 'function';
    }
    return false;
  }

  private isMultipartOrBinaryBody(body: unknown): boolean {
    if (body == null) return false;
    if (this.isFormDataBody(body)) return true;
    if (typeof File !== 'undefined' && body instanceof File) return true;
    if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
    if (body instanceof ArrayBuffer) return true;
    if (typeof body === 'object') {
      const o = body as Record<string, unknown>;
      if (typeof o.slice === 'function' && 'type' in o) return true;
    }
    return false;
  }

  /**
   * Fetch-to-proxy (or direct) abort deadline. Configured Proxy Settings timeout is the
   * source of truth for the normal path. Multipart/binary keeps the existing long floor.
   */
  private computeProxyFetchTimeoutMs(requestData: ProxyRequest): number {
    return resolveProxyFetchTimeoutMs({
      configuredMs: this.getRequestTimeoutMs(),
      explicitMs: requestData.timeout,
      isMultipartOrBinary: this.isMultipartOrBinaryBody(requestData.body),
    });
  }

  public async sendRequest(requestData: ProxyRequest): Promise<ProxyResponse> {
    console.log('=== PROXY SERVICE DEBUG ===');
    console.log('ProxyService: sendRequest called');
    console.log('ProxyService: Current proxy available status:', this.isProxyAvailable);
    console.log('ProxyService: Original request data:', requestData);

    // Last-mile $function resolution for string bodies (JSON/raw). Form-data fields are
    // already resolved when FormData is built; binary bodies are left untouched.
    requestData = await this.resolveFunctionsInProxyRequest(requestData);
    
    // CRITICAL: Validate URL before processing
    if (!requestData.url || typeof requestData.url !== 'string') {
      throw new Error(`Invalid URL: URL must be a non-empty string. Got: ${typeof requestData.url}`);
    }
    
    let urlToValidate = String(requestData.url).trim();

    // If the URL is relative (no protocol), try to make it absolute using the current origin
    if (!urlToValidate.startsWith('http://') && !urlToValidate.startsWith('https://')) {
      try {
        const origin = window.location.origin;
        const cleanedPath = urlToValidate.replace(/^\/+/, '');
        urlToValidate = `${origin}/${cleanedPath}`;
        requestData.url = urlToValidate;
        console.log('ProxyService: Converted relative URL to absolute using window.location.origin:', urlToValidate);
      } catch {
        // If we cannot resolve origin (e.g., non-browser env), fall through to normal validation and error messaging
      }
    }

    console.log('ProxyService: Validating URL:', urlToValidate);
    
    // Validate URL format
    try {
      const testUrl = new URL(urlToValidate);
      if (!testUrl.hostname || testUrl.hostname.trim().length === 0) {
        throw new Error('URL hostname is empty');
      }
      if (testUrl.hostname === 'http' || testUrl.hostname === 'https') {
        throw new Error(`URL hostname is "${testUrl.hostname}" - this indicates a malformed URL. Original URL: "${urlToValidate}"`);
      }
      console.log('ProxyService: URL validation passed:', {
        hostname: testUrl.hostname,
        protocol: testUrl.protocol,
        pathname: testUrl.pathname
      });
    } catch (urlError: any) {
      console.error('ProxyService: URL validation failed:', {
        url: urlToValidate,
        error: urlError.message,
        urlType: typeof requestData.url,
        urlLength: requestData.url?.length
      });
      
      // Provide concise user-friendly error message
      let userFriendlyMessage = '';
      
      // Check if URL is missing protocol (http:// or https://)
      if (!urlToValidate.startsWith('http://') && !urlToValidate.startsWith('https://')) {
        // Check if it looks like a URL (has hostname:port or hostname/path pattern)
        if (urlToValidate.includes(':') || urlToValidate.includes('/') || urlToValidate.includes('.')) {
          userFriendlyMessage = `URL is missing http:// or https://. Please add the protocol to: ${urlToValidate}`;
        } else {
          userFriendlyMessage = `Invalid URL format: ${urlToValidate}. URL must include protocol (http:// or https://)`;
        }
      } else if (urlError.message.includes('hostname is empty') || urlError.message.includes('Invalid URL')) {
        userFriendlyMessage = `Invalid URL format: ${urlToValidate}. Please check the URL and try again.`;
      } else {
        userFriendlyMessage = `Invalid URL: ${urlToValidate}. ${urlError.message}`;
      }
      
      throw new Error(userFriendlyMessage);
    }
    
    // Use the headers as provided (from Authorization tab)
    const headers = { ...requestData.headers };
    console.log('🔧 Using headers from request (Authorization tab):', headers);

    // Postman-style binary upload: ensure Content-Type is set from file when body is File/Blob
    // so the proxy never defaults to application/json (which would break .txt and other binary types)
    const isBinaryBody = requestData.body != null && (
      (typeof File !== 'undefined' && requestData.body instanceof File) ||
      requestData.body instanceof Blob ||
      requestData.body instanceof ArrayBuffer
    );
    if (isBinaryBody && (requestData.body instanceof Blob || (typeof File !== 'undefined' && requestData.body instanceof File))) {
      const blob = requestData.body as Blob;
      const ct = (headers['Content-Type'] ?? headers['content-type'] ?? '').trim();
      if (!ct) {
        const binaryType = blob.type && blob.type.trim() ? blob.type : 'application/octet-stream';
        headers['Content-Type'] = binaryType;
        if (headers['content-type'] !== undefined) delete headers['content-type'];
        console.log('ProxyService: Set Content-Type for binary body:', binaryType);
      }
    }

    // ALWAYS use proxy for ALL requests - no bypass logic
    console.log('🚫 Should bypass proxy: FALSE (all requests through proxy)');

    const proxyFetchTimeoutMs = this.computeProxyFetchTimeoutMs(requestData);
    requestData = { ...requestData, timeout: proxyFetchTimeoutMs };

    if (this.proxyMode === 'auto') {
      console.log('ProxyService: Server Proxy mode — using backend /api/proxy');
      return this.sendRequestViaBackendProxy(requestData);
    }

    if (this.hostedLocalAgentRecentlyDown()) {
      console.log('ProxyService: Local agent recently unavailable on hosted app — using backend /api/proxy');
      return this.sendRequestViaBackendProxy(requestData);
    }

    // Check proxy availability if not already checked
    if (!this.isProxyAvailable) {
      console.log('ProxyService: Proxy not available, checking...');
      const isAvailable = await this.checkProxyAvailability();
      if (!isAvailable) {
        if (this.isHostedAppEnvironment()) {
          console.log('ProxyService: Local agent unavailable on hosted app — using backend /api/proxy');
          return this.sendRequestViaBackendProxy(requestData);
        }
        throw new Error(`${DESKTOP_AGENT_NAME} is not running. Install and start it on http://localhost:9000.`);
      }
    }

    // Small multipart/binary: try browser-direct when CORS allows. Large files use local proxy only
    // (direct uploads of videos often hang or fail preflight; proxy streams the full body reliably).
    if (this.isMultipartOrBinaryBody(requestData.body)) {
      const estimatedUploadBytes = this.estimateUploadBodyBytes(requestData.body);
      const tryDirectFirst =
        estimatedUploadBytes == null ||
        estimatedUploadBytes <= ProxyService.MULTIPART_DIRECT_MAX_BYTES;
      if (tryDirectFirst) {
        console.log(
          'ProxyService: multipart/binary upload' +
            (estimatedUploadBytes != null
              ? ` (~${Math.round(estimatedUploadBytes / (1024 * 1024))} MB)`
              : '') +
            ', trying direct to target first'
        );
        try {
          return await this.makeDirectRequest(requestData);
        } catch (directErr) {
          if (this.isCorsError(directErr)) {
            console.log('ProxyService: Direct upload blocked by CORS, using local proxy');
          } else {
            console.warn('ProxyService: Direct upload failed, using local proxy', directErr);
          }
        }
      } else {
        console.log(
          'ProxyService: large multipart upload (~' +
            Math.round(estimatedUploadBytes / (1024 * 1024)) +
            ' MB), using local proxy'
        );
      }
    }

    try {
      console.log('ProxyService: Sending request via proxy:', { ...requestData, headers });
      console.log('ProxyService: URL being requested:', requestData.url);
      console.log('ProxyService: Method:', requestData.method);
      console.log('ProxyService: Headers:', headers);
      console.log('ProxyService: Timeout (requested / effective ms):', requestData.timeout, '/', proxyFetchTimeoutMs);
      
      const startTime = Date.now();
      
      // ALL requests go through proxy (like Postman) - no local request bypass
      const isLocalRequest = false; // Always false to ensure all requests go through proxy
      const proxyUrl = appendProxyTimeoutQuery(
        this.buildProxyUrl(requestData.url, isLocalRequest),
        proxyFetchTimeoutMs
      );
      
      console.log('🌐 Is local request:', isLocalRequest);
      console.log('🔗 Proxy URL:', proxyUrl);
      
      // Prepare fetch options with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), proxyFetchTimeoutMs);
      
      // Handle FormData separately (for multipart/form-data). Postman-style: send other headers (e.g. Authorization) but let browser set Content-Type with boundary.
      // Duck-type FormData for cross-realm/bundled envs where instanceof can fail
      const isFormData = this.isFormDataBody(requestData.body);
      const headersToSend = applyProxyTimeoutHeader(
        isFormData
          ? (() => { const h = { ...headers }; delete h['Content-Type']; delete h['content-type']; return h; })()
          : { ...headers },
        proxyFetchTimeoutMs
      );
      const fetchOptions: LocalProxyFetchInit = {
        method: requestData.method,
        headers: headersToSend,
        signal: controller.signal,
        mode: 'cors',
        // Chromium LNA: public HTTPS page → http://localhost proxy
        targetAddressSpace: 'loopback',
      };
      
      // Add body for methods that allow a payload (never for GET/HEAD/OPTIONS)
      // Postman-style: File/Blob/ArrayBuffer must be sent as-is (binary upload), never JSON.stringify
      const methodUpper = String(requestData.method || 'GET').toUpperCase();
      const allowRequestBody =
        methodUpper !== 'GET' && methodUpper !== 'HEAD' && methodUpper !== 'OPTIONS';
      if (allowRequestBody && requestData.body !== undefined && requestData.body !== null) {
        if (isFormData) {
          fetchOptions.body = requestData.body;
          // Don't set Content-Type header for FormData - browser will set it with boundary
        } else if (typeof requestData.body === 'string') {
          fetchOptions.body = requestData.body;
        } else if (requestData.body instanceof Blob || requestData.body instanceof ArrayBuffer) {
          fetchOptions.body = requestData.body;
          // Headers (incl. Content-Type from file) set above for binary
        } else if (typeof File !== 'undefined' && requestData.body instanceof File) {
          fetchOptions.body = requestData.body;
          // File: Content-Type already set from file.type above
        } else if (
          typeof requestData.body === 'object' &&
          requestData.body !== null &&
          typeof (requestData.body as Blob).slice === 'function' &&
          'type' in requestData.body
        ) {
          // Duck-type Blob/File (handles cross-realm or bundled envs where instanceof can fail)
          fetchOptions.body = requestData.body as Blob;
          const blob = requestData.body as Blob;
          const ct = (headers['Content-Type'] ?? headers['content-type'] ?? '').trim();
          if (!ct && blob.type) {
            headers['Content-Type'] = blob.type.trim() || 'application/octet-stream';
            if (headers['content-type'] !== undefined) delete headers['content-type'];
          }
        } else {
          fetchOptions.body = JSON.stringify(requestData.body);
        }
      }
      
      console.log('📤 Final fetch options:', fetchOptions);
      console.log('=== END PROXY SERVICE DEBUG ===');
      
      try {
        // Make the request through the proxy
        const response = await fetch(proxyUrl, fetchOptions);
        clearTimeout(timeoutId);
        
        const endTime = Date.now();
        
        console.log('ProxyService: Response received in', endTime - startTime, 'ms');
        console.log('ProxyService: Response status:', response.status);
        
        // Get response data
        const contentType = response.headers.get('content-type') || '';
        let responseData;
        
        console.log('ProxyService: Response content-type:', contentType);

        // Read the full response body as text. Postman-style: we never truncate; the entire
        // payload (including large Base64, 1000+ records, multi-MB) is preserved and returned.
        const responseText = await response.text();

        // Postman-style: 200 OK with content-type application/json and empty body is valid; normalize to {} so parsers never see "".
        const isEmptyJson = contentType.includes('application/json') && (!responseText || responseText.trim() === '');
        if (isEmptyJson) {
          responseData = {};
        } else {
          // For very large JSON, pass through as raw string to avoid JSON.parse allocation/failure
          // and double memory when frontend stringifies again. No artificial size limit on payload.
          const LARGE_JSON_PASSTHROUGH = 2 * 1024 * 1024; // 2MB
          const isLargeJson = contentType.includes('application/json') && responseText.length > LARGE_JSON_PASSTHROUGH;

          if (contentType.includes('application/json') && !isLargeJson) {
            try {
              responseData = JSON.parse(responseText);
            } catch (jsonError) {
              console.warn('ProxyService: Failed to parse JSON, using raw text instead:', jsonError);
              responseData = responseText;
            }
          } else if (isLargeJson || !contentType.includes('application/json')) {
            // Large JSON or non-JSON: use full text directly (no parse, no truncation).
            responseData = responseText;
          }
        }
        
        console.log('ProxyService: Response data type:', typeof responseData);
        
        if (typeof responseData === 'string') {
          console.log('ProxyService: Response data size:', responseData.length, 'characters');
        }
        
        // Collect response headers
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          responseHeaders[key] = value;
        });

        const result = {
          status: response.status,
          statusText: response.statusText,
          headers: responseHeaders,
          data: responseData,
          url: response.url
        };

        const isFormDataBody = this.isFormDataBody(requestData.body);
        const isBinaryBody =
          requestData.body != null &&
          (requestData.body instanceof Blob ||
            requestData.body instanceof ArrayBuffer ||
            (typeof File !== 'undefined' && requestData.body instanceof File));
        const isLargeBodyProxyLimitError =
          (result.status === 502 || result.status === 413) &&
          (() => {
            const payload =
              typeof result.data === 'string'
                ? result.data
                : (() => {
                    try {
                      return JSON.stringify(result.data ?? '');
                    } catch {
                      return String(result.data ?? '');
                    }
                  })();
            return (
              payload.includes('maxBodyLength') ||
              payload.includes('Request body larger than maxBodyLength limit')
            );
          })();
        if ((isFormDataBody || isBinaryBody) && isLargeBodyProxyLimitError) {
          console.warn('ProxyService: proxy body-size limit hit for file upload, retrying direct request');
          try {
            const directResult = await this.makeDirectRequest(requestData);
            return directResult;
          } catch (directErr) {
            console.warn('ProxyService: direct upload retry failed after proxy body-size limit', directErr);
          }
        }

        this.addProxyLogEntry({
          method: requestData.method,
          url: requestData.url,
          status: response.status,
          statusText: response.statusText,
          durationMs: endTime - startTime,
          requestHeaders: requestData.headers,
          responseHeaders,
          requestBody: await this.safeBodyForLog(requestData.body),
          responseBody: await this.safeResponseBodyForLog(responseData),
        });

        return result;
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
          throw new Error(formatRequestTimedOutError(proxyFetchTimeoutMs));
        }
        throw error;
      }
      
    } catch (error: any) {
      this.addProxyLogEntry({
        method: requestData.method,
        url: requestData.url,
        error: error?.message ?? String(error),
        requestHeaders: requestData.headers,
        requestBody: await this.safeBodyForLog(requestData.body),
      });
      console.error('ProxyService: Request failed with error:', error);
      console.error('ProxyService: Error type:', typeof error);
      console.error('ProxyService: Error message:', error.message);
      console.error('ProxyService: Error stack:', error.stack);
      
      // Do not map parse/response errors to proxy unavailable (proxy worked; failure is after response)
      if (error.name === 'SyntaxError' || (error.message && error.message.includes('Unexpected end of JSON input'))) {
        throw error;
      }
      
      // Check if it's a proxy server / network error (fetch failed, not response parsing)
      if (error.message && error.message.includes('Failed to fetch')) {
        console.log('ProxyService: Failed to fetch, re-checking proxy availability...');
        await this.checkProxyAvailability();
        if (this.isHostedAppEnvironment()) {
          console.log('ProxyService: Retrying via backend /api/proxy after local agent failure');
          return this.sendRequestViaBackendProxy(requestData);
        }
        if (this.isProxyAvailable) {
          throw new Error(
            `${DESKTOP_AGENT_NAME} is running, but the browser blocked the request (CORS). Restart the agent from this project's proxy-server folder and retry.`
          );
        }
        throw new Error(`${DESKTOP_AGENT_NAME} is not available. Install and start it on http://localhost:9000.`);
      }
      
      // Check if it's a timeout error
      if (error.message && error.message.includes('timeout')) {
        throw new Error(
          `${formatRequestTimedOutError(proxyFetchTimeoutMs)} The response might be too large or the server is slow.`
        );
      }
      
      // Check if it's a network error
      if (error.message && error.message.includes('NetworkError')) {
        throw new Error('Network error. Please check your internet connection and ensure the proxy server is running.');
      }
      
      throw error;
    }
  }

  private async makeDirectRequest(requestData: ProxyRequest): Promise<ProxyResponse> {
    const startTime = Date.now();
    try {
      console.log('ProxyService: Making direct request to:', requestData.url);
      
      // Headers already contain JWT token from sendRequest method
      const headers = { ...requestData.headers };
      console.log('ProxyService: Using headers with JWT token:', headers);

      // Binary body: ensure Content-Type from file (Postman-style)
      const body = requestData.body;
      if (body != null && (body instanceof Blob || (typeof File !== 'undefined' && body instanceof File))) {
        const ct = (headers['Content-Type'] ?? headers['content-type'] ?? '').trim();
        if (!ct) {
          const binaryType = (body as Blob).type?.trim() || 'application/octet-stream';
          headers['Content-Type'] = binaryType;
          if (headers['content-type'] !== undefined) delete headers['content-type'];
        }
      }
      // Postman-style: for FormData, remove Content-Type so browser sets multipart boundary
      if (this.isFormDataBody(requestData.body)) {
        delete headers['Content-Type'];
        delete headers['content-type'];
      }
      
      const fetchTimeoutMs = this.computeProxyFetchTimeoutMs(requestData);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), fetchTimeoutMs);
      
      const fetchOptions: RequestInit = {
        method: requestData.method,
        headers,
        signal: controller.signal,
        mode: 'cors'
      };
      
      // Add body for methods that allow a payload (never for GET/HEAD/OPTIONS)
      // Postman-style: File/Blob as-is, never JSON.stringify
      const directMethodUpper = String(requestData.method || 'GET').toUpperCase();
      const allowDirectBody =
        directMethodUpper !== 'GET' && directMethodUpper !== 'HEAD' && directMethodUpper !== 'OPTIONS';
      if (allowDirectBody && requestData.body !== undefined && requestData.body !== null) {
        if (this.isFormDataBody(requestData.body)) {
          fetchOptions.body = requestData.body as FormData;
        } else if (typeof requestData.body === 'string') {
          fetchOptions.body = requestData.body;
        } else if (requestData.body instanceof Blob || requestData.body instanceof ArrayBuffer) {
          fetchOptions.body = requestData.body;
        } else if (typeof File !== 'undefined' && requestData.body instanceof File) {
          fetchOptions.body = requestData.body;
        } else {
          fetchOptions.body = JSON.stringify(requestData.body);
        }
      }
      
      let response: Response;
      try {
        response = await fetch(requestData.url, fetchOptions);
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          throw new Error(formatRequestTimedOutError(fetchTimeoutMs));
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
      
      // Get response data
      const contentType = response.headers.get('content-type') || '';
      let responseData;
      
      const responseText = await response.text();
      
      // Postman-style: 200 OK with application/json and empty body -> {} so parsers never see "".
      const isEmptyJson = contentType.includes('application/json') && (!responseText || responseText.trim() === '');
      if (isEmptyJson) {
        responseData = {};
      } else {
        // Postman-style: For very large JSON, pass through as raw string to avoid JSON.parse
        // allocation/failure (same as proxy route). No truncation; full payload preserved.
        const LARGE_JSON_PASSTHROUGH = 2 * 1024 * 1024; // 2MB
        const isLargeJson = contentType.includes('application/json') && responseText.length > LARGE_JSON_PASSTHROUGH;

        if (contentType.includes('application/json') && !isLargeJson) {
          try {
            responseData = JSON.parse(responseText);
          } catch (jsonError) {
            responseData = responseText;
          }
        } else {
          responseData = responseText;
        }
      }
      
      // Collect response headers
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      const endTime = Date.now();
      this.addProxyLogEntry({
        method: requestData.method,
        url: requestData.url,
        status: response.status,
        statusText: response.statusText,
        durationMs: endTime - startTime,
        requestHeaders: requestData.headers,
        responseHeaders,
        requestBody: await this.safeBodyForLog(requestData.body),
        // Form-data/binary often use this direct path — keep full body (Console UI applies preview limits).
        responseBody: await this.safeResponseBodyForLog(responseData),
      });
      
      return {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        data: responseData,
        url: response.url
      };
      
    } catch (error: any) {
      console.error('ProxyService: Direct request failed:', error);
      
      // Check if it's a CORS error
      if (this.isCorsError(error)) {
        console.log('ProxyService: CORS error detected, falling back to proxy');
        return this.fallbackToProxy(requestData);
      }
      
      throw error;
    }
  }

  private isCorsError(error: any): boolean {
    // Check for various CORS error patterns
    const errorMessage = error.message?.toLowerCase() || '';
    const errorName = error.name?.toLowerCase() || '';
    
    return (
      errorMessage.includes('cors') ||
      errorMessage.includes('cross-origin') ||
      errorMessage.includes('access-control') ||
      (errorName === 'typeerror' && errorMessage.includes('failed to fetch')) ||
      errorMessage.includes('blocked by cors policy')
    );
  }

  private async fallbackToProxy(requestData: ProxyRequest): Promise<ProxyResponse> {
    console.log('ProxyService: Falling back to proxy for CORS handling');
    const startTime = Date.now();
    
    // Check proxy availability
    if (!this.isProxyAvailable) {
      const isAvailable = await this.checkProxyAvailability();
      if (!isAvailable) {
        throw new Error(
          `CORS error detected and ${DESKTOP_AGENT_NAME} is not available. Install and start it on http://localhost:9000 to handle requests like Postman.`,
        );
      }
    }
    
    // Use the proxy for this request (treat as external)
    const fetchTimeoutMs = this.computeProxyFetchTimeoutMs(requestData);
    const proxyUrl = appendProxyTimeoutQuery(this.buildProxyUrl(requestData.url, false), fetchTimeoutMs);
    console.log('ProxyService: Using proxy URL:', proxyUrl);
    
    const headers = { ...requestData.headers };
    const body = requestData.body;
    if (body != null && (body instanceof Blob || (typeof File !== 'undefined' && body instanceof File))) {
      const ct = (headers['Content-Type'] ?? headers['content-type'] ?? '').trim();
      if (!ct) {
        const binaryType = (body as Blob).type?.trim() || 'application/octet-stream';
        headers['Content-Type'] = binaryType;
        if (headers['content-type'] !== undefined) delete headers['content-type'];
      }
    }
    // Postman-style: for FormData, remove Content-Type so browser sets multipart boundary
    if (this.isFormDataBody(requestData.body)) {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), fetchTimeoutMs);
    
    const fetchOptions: LocalProxyFetchInit = {
      method: requestData.method,
      headers: applyProxyTimeoutHeader(headers, fetchTimeoutMs),
      signal: controller.signal,
      mode: 'cors',
      targetAddressSpace: 'loopback',
    };
    
    // Postman-style: File/Blob/ArrayBuffer/FormData sent as-is (binary upload), never JSON.stringify
    // Never attach a body for GET/HEAD/OPTIONS.
    const retryMethodUpper = String(requestData.method || 'GET').toUpperCase();
    const allowRetryBody =
      retryMethodUpper !== 'GET' && retryMethodUpper !== 'HEAD' && retryMethodUpper !== 'OPTIONS';
    if (allowRetryBody && requestData.body !== undefined && requestData.body !== null) {
      if (this.isFormDataBody(requestData.body)) {
        fetchOptions.body = requestData.body as FormData;
      } else if (typeof requestData.body === 'string') {
        fetchOptions.body = requestData.body;
      } else if (requestData.body instanceof Blob || requestData.body instanceof ArrayBuffer) {
        fetchOptions.body = requestData.body;
      } else if (typeof File !== 'undefined' && requestData.body instanceof File) {
        fetchOptions.body = requestData.body;
      } else {
        fetchOptions.body = JSON.stringify(requestData.body);
      }
    }
    
    let response: Response;
    try {
      response = await fetch(proxyUrl, fetchOptions);
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        throw new Error(formatRequestTimedOutError(fetchTimeoutMs));
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
    
    const contentType = response.headers.get('content-type') || '';
    let responseData;
    
    const responseText = await response.text();
    
    // Postman-style: application/json with empty body -> {} so parsers never see "".
    const isEmptyJson = contentType.includes('application/json') && (!responseText || responseText.trim() === '');
    if (isEmptyJson) {
      responseData = {};
    } else {
      // Postman-style: For very large JSON, pass through as raw string (no parse, no truncation).
      const LARGE_JSON_PASSTHROUGH = 2 * 1024 * 1024; // 2MB
      const isLargeJson = contentType.includes('application/json') && responseText.length > LARGE_JSON_PASSTHROUGH;

      if (contentType.includes('application/json') && !isLargeJson) {
        try {
          responseData = JSON.parse(responseText);
        } catch (jsonError) {
          responseData = responseText;
        }
      } else {
        responseData = responseText;
      }
    }
    
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    const endTime = Date.now();
    this.addProxyLogEntry({
      method: requestData.method,
      url: requestData.url,
      status: response.status,
      statusText: response.statusText,
      durationMs: endTime - startTime,
      requestHeaders: requestData.headers,
      responseHeaders,
      requestBody: await this.safeBodyForLog(requestData.body),
      // Same as proxy path: do not truncate (form-data/binary CORS fallback lands here).
      responseBody: await this.safeResponseBodyForLog(responseData),
    });
    
    return {
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      data: responseData,
      url: response.url
    };
  }

  private buildProxyUrl(originalUrl: string, isLocalRequest: boolean): string {
    console.log('🔧 buildProxyUrl called with:', { originalUrl, isLocalRequest });
    
    // Normalize the URL first - trim whitespace
    let normalizedUrl = String(originalUrl).trim();
    
    try {
      const url = new URL(normalizedUrl);
      console.log('🔍 Parsed URL:', {
        host: url.host,
        hostname: url.hostname,
        port: url.port,
        pathname: url.pathname,
        search: url.search,
        protocol: url.protocol
      });
      
      // Validate that hostname is not empty
      if (!url.hostname || url.hostname.trim().length === 0) {
        throw new Error('URL hostname is empty');
      }
      
      // Validate that hostname is not just "http" or "https"
      if (url.hostname === 'http' || url.hostname === 'https') {
        throw new Error(`URL hostname is "${url.hostname}" - this indicates a malformed URL`);
      }
      
      // ALWAYS use /proxy/ endpoint for ALL requests (like Postman)
      // Include the protocol in the path so the backend can use it
      const protocol = url.protocol.replace(':', ''); // Remove colon from 'http:' or 'https:'
      
      // Encode query exactly once (100+2 → 100%2B2) before wrapping for Express.
      const targetUrlString = toAbsoluteWireUrl(normalizedUrl);
      
      // CRITICAL: URL-encode the target URL string to prevent Express from splitting on '/'
      // The proxy server will decode it back
      const encodedTargetUrl = encodeURIComponent(targetUrlString);
      
      const proxyUrl = `${this.getProxyBaseUrl()}/proxy/${encodedTargetUrl}`;
      
      console.log('🌐 Proxy URL (all requests through proxy):', proxyUrl);
      console.log('🌐 Target URL string (original):', targetUrlString);
      console.log('🌐 Target URL string (encoded):', encodedTargetUrl);
      console.log('🌐 Protocol:', protocol);
      console.log('🌐 Host:', url.host);
      console.log('🌐 Pathname:', url.pathname);
      console.log('🌐 Search:', url.search);
      
      return proxyUrl;
    } catch (error: any) {
      console.warn('⚠️ URL parsing failed:', error.message);
      console.warn('⚠️ Original URL:', normalizedUrl);
      
      // If URL parsing fails, try to extract host and path manually
      // Remove protocol if present
      let urlWithoutProtocol = normalizedUrl;
      if (urlWithoutProtocol.startsWith('http://')) {
        urlWithoutProtocol = urlWithoutProtocol.substring(7);
      } else if (urlWithoutProtocol.startsWith('https://')) {
        urlWithoutProtocol = urlWithoutProtocol.substring(8);
      }
      
      // Trim any leading slashes
      urlWithoutProtocol = urlWithoutProtocol.replace(/^\/+/, '');
      
      // Validate that we have something after removing protocol
      if (!urlWithoutProtocol || urlWithoutProtocol.trim().length === 0) {
        console.error('❌ URL is empty after removing protocol');
        throw new Error(`Invalid URL: "${originalUrl}" - cannot extract hostname`);
      }
      
      // Use /local/ endpoint - it will add http:// automatically
      // IMPORTANT: Don't include http:// in the path, as /local/ adds it
      const proxyUrl = `${this.getProxyBaseUrl()}/local/${urlWithoutProtocol}`;
      console.log('⚠️ Using /local/ endpoint with normalized URL:', proxyUrl);
      console.log('⚠️ URL without protocol:', urlWithoutProtocol);
      
      return proxyUrl;
    }
  }

  // Method to manually check proxy availability
  public async checkProxyStatus(): Promise<boolean> {
    return await this.checkProxyAvailability();
  }

  // Method to test proxy connection with detailed logging
  public async testProxyConnection(): Promise<{ success: boolean; message: string; details?: any }> {
    console.log('ProxyService: Testing proxy connection...');

    if (this.proxyMode === 'auto') {
      const serverUrl = this.getServerProxyUrl();
      try {
        const ok = await this.probeServerProxy();
        this.setConnectionAvailable(ok);
        if (ok) {
          return {
            success: true,
            message: 'Server proxy is reachable',
            details: { url: serverUrl },
          };
        }
        return {
          success: false,
          message: 'Server proxy is not responding. Confirm the API backend is running.',
          details: { url: serverUrl },
        };
      } catch (error: any) {
        this.setConnectionAvailable(false);
        return {
          success: false,
          message: `Failed to connect to server proxy: ${error?.message || 'Unknown error'}`,
          details: { url: serverUrl },
        };
      }
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const healthUrl = `${this.getProxyBaseUrl()}/health`;
      
      console.log('ProxyService: Making test request to:', healthUrl);
      
      // No Content-Type on GET (avoids extra CORS preflight). targetAddressSpace for Chromium LNA.
      const response = await fetch(
        healthUrl,
        localProxyFetchInit({
          method: 'GET',
          signal: controller.signal,
        })
      );
      
      clearTimeout(timeoutId);
      
      console.log('ProxyService: Test response status:', response.status);
      console.log('ProxyService: Test response ok:', response.ok);
      console.log('ProxyService: Test response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const data = await response.json();
        console.log('ProxyService: Test response data:', data);

        const status = String(data?.status || '').toUpperCase();
        const message = String(data?.message || '').toLowerCase();
        const isNodeProxy = status === 'OK' && message.includes('api testing proxy server');
        if (!isNodeProxy) {
          this.setConnectionAvailable(false);
          return {
            success: false,
            message:
              `A server responded at this URL, but it is not the ${DESKTOP_AGENT_NAME}. Set the proxy URL to http://localhost:9000 and ensure the desktop agent is installed and running.`,
            details: data,
          };
        }

        this.setConnectionAvailable(true);
        return {
          success: true,
          message: 'Proxy server is accessible',
          details: data
        };
      } else {
        const errorText = await response.text();
        console.log('ProxyService: Test response error text:', errorText);
        
        this.setConnectionAvailable(false);
        return {
          success: false,
          message: `Proxy server responded with status ${response.status}`,
          details: { status: response.status, text: errorText }
        };
      }
    } catch (error: any) {
      console.error('ProxyService: Test connection error:', error);
      
      this.setConnectionAvailable(false);
      const raw = String(error?.message || 'Unknown error');
      const isFetchFail = /failed to fetch|networkerror|load failed/i.test(raw);
      const message = isFetchFail
        ? chromeInsecureLocalProxyMessage(raw)
        : `Connection failed: ${raw}`;

      return {
        success: false,
        message,
        details: { 
          name: error.name, 
          message: error.message,
          type: error.constructor.name,
          pageHost: typeof window !== 'undefined' ? window.location.hostname : '',
          insecureRemoteContext: isBrowserOnRemoteInsecureHost(),
          isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
        }
      };
    }
  }

  // Method to test CORS compatibility for a URL
  public async testCorsCompatibility(url: string): Promise<{ 
    corsCompatible: boolean; 
    message: string; 
    shouldUseProxy: boolean;
  }> {
    try {
      console.log('ProxyService: Testing CORS compatibility for:', url);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(url, {
        method: 'OPTIONS', // Preflight request
        signal: controller.signal,
        mode: 'cors'
      });
      
      clearTimeout(timeoutId);
      
      // Check if CORS headers are present
      const corsHeaders = [
        'access-control-allow-origin',
        'access-control-allow-methods',
        'access-control-allow-headers'
      ];
      
      const hasCorsHeaders = corsHeaders.some(header => 
        response.headers.has(header)
      );
      
      if (hasCorsHeaders) {
        return {
          corsCompatible: true,
          message: 'CORS is properly configured. Direct requests should work.',
          shouldUseProxy: false
        };
      } else {
        return {
          corsCompatible: false,
          message: 'CORS headers not found. Proxy will be used for CORS handling.',
          shouldUseProxy: true
        };
      }
      
    } catch (error: any) {
      console.log('ProxyService: CORS test failed:', error.message);
      return {
        corsCompatible: false,
        message: `CORS test failed: ${error.message}. Proxy will be used.`,
        shouldUseProxy: true
      };
    }
  }

  // Method to get proxy server info
  public getProxyInfo() {
    const baseUrl = this.getProxyBaseUrl();
    return {
      baseUrl: baseUrl,
      isAvailable: this.isProxyAvailable,
      healthCheckUrl: `${baseUrl}/health`,
      bypassUrls: this.getBypassUrls()
    };
  }
}

export const proxyService = new ProxyService();
export default proxyService;
